import React, { useState, useEffect, useRef } from "react";
import {
  isEncryptedDocumentUrl,
  decryptBlob,
} from "../utils/documentEncryption";

/**
 * Image component that fetches and decrypts document photos when the URL
 * points to an encrypted file (.enc). Use for Aadhar, PAN, bank passbook,
 * electricity bill, etc.
 */
function DecryptedImage({ src, alt = "Document", className = "", ...props }) {
  const [objectUrl, setObjectUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const blobUrlRef = useRef(null);

  useEffect(() => {
    if (!src) {
      setObjectUrl(null);
      setError(false);
      return;
    }

    if (!isEncryptedDocumentUrl(src)) {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      setObjectUrl(src);
      setError(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await fetch(src);
        if (!res.ok) throw new Error("Failed to fetch");
        const blob = await res.blob();
        const decrypted = await decryptBlob(blob);
        if (!decrypted || cancelled) return;
        const url = URL.createObjectURL(decrypted);
        blobUrlRef.current = url;
        setObjectUrl(url);
      } catch (e) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [src]);

  if (loading) {
    return (
      <div
        className={`flex items-center justify-center bg-muted ${className}`}
        {...props}
      >
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error || !objectUrl) {
    return (
      <div
        className={`flex items-center justify-center bg-muted text-muted-foreground ${className}`}
        {...props}
      >
        {error ? "Unable to load image" : ""}
      </div>
    );
  }

  return (
    <img
      src={objectUrl}
      alt={alt}
      className={className}
      onError={() => setError(true)}
      {...props}
    />
  );
}

export default DecryptedImage;
